// Initialize pdf-lib functions
let PDFDocument: any;
let rgb: any;

// Load pdf-lib functions synchronously (for compatibility)
function initializePDFLib() {
  if (!PDFDocument || !rgb) {
    // Try to get from window first (production)
    if (window.PDFLib) {
      PDFDocument = window.PDFLib.PDFDocument;
      rgb = window.PDFLib.rgb;
    } else {
      // Fallback to default values (will be set later)
      console.warn("PDFLib not available, using fallback values");
    }
  }
}

import {
  HeaderOptions,
  HeaderResult,
  PdfGeneratorOptions,
  RuleData,
  RuleType,
  TdaRulesData,
  TextBlockResult,
} from "../types/pdf-generator";
import { drawHorizontalLine, drawRoundedRectangle } from "./graphicDraw";
import RulesLinker from "./rulesLinker";
import { cleanTextForPdf, hexToRgb, truncateText } from "./textUtils";
import TextWriter from "./textWriter";

export class RulesGenerator {
  private pdfDoc: typeof PDFDocument;
  // private pageSize: PageSize;
  private rulesLinker: RulesLinker;
  private textWriter: TextWriter;
  private pdfOptions: PdfGeneratorOptions;
  private rulesToTrace: string[] | null = null;
  private rulesData: TdaRulesData;
  private logoBuffer: ArrayBuffer | null;

  private columnWidth = 0;
  private columnGap = 12;
  private effectiveColumnWidth;
  private currentColumn = 0;
  private fullWidth: boolean = false;
  private currentPage: any | null = null;
  private pageStartY: number = 0;
  private pageTopY: number = 0;
  private marginLeft: number = 0;

  constructor(
    pdfDoc: typeof PDFDocument,
    pdfOptions: PdfGeneratorOptions,
    rulesData: TdaRulesData,
    logoBuffer: ArrayBuffer | null
  ) {
    // Initialize pdf-lib functions
    initializePDFLib();

    this.pdfDoc = pdfDoc;
    this.pdfOptions = pdfOptions;
    this.rulesLinker = new RulesLinker(pdfDoc);
    this.textWriter = new TextWriter(
      pdfDoc,
      pdfOptions.pageSize,
      pdfOptions.fonts.regular,
      this.rulesLinker
    );
    this.rulesData = rulesData;
    if (rulesData.meta.rulesToTrace && rulesData.meta.rulesToTrace.length > 0) {
      console.log("rulesToTrace", rulesData.meta.rulesToTrace);
      this.rulesToTrace = rulesData.meta.rulesToTrace;
    }
    this.logoBuffer = logoBuffer || null;

    this.columnWidth =
      Math.round(
        (10 *
          (this.pdfOptions.pageSize.width -
            this.pdfOptions.pageSize.marginLeft -
            this.pdfOptions.pageSize.marginRight)) /
          this.pdfOptions.columnCount
      ) / 10;
    this.columnGap = 12;
    this.effectiveColumnWidth =
      this.pdfOptions.columnCount > 1
        ? this.columnWidth - this.columnGap
        : this.columnWidth;
    this.pageTopY =
      this.pdfOptions.pageSize.height - this.pdfOptions.pageSize.marginTop;
    this.marginLeft = this.pdfOptions.pageSize.marginLeft;
  }

  private newPage = () => {
    return this.pdfDoc.addPage([
      this.pdfOptions.pageSize.width,
      this.pdfOptions.pageSize.height,
    ]);
  };

  getMaxWidth(): number {
    return this.effectiveColumnWidth;
  }

  getMarginLeft(): number {
    return this.marginLeft;
  }

  setPageStartY(y: number): void {
    this.pageStartY = y;
  }

  addPage(fullWidth: boolean = false): {
    page: any;
    y: number;
    x: number;
    width: number;
  } {
    if (
      fullWidth ||
      this.fullWidth ||
      this.pdfOptions.columnCount === 1 ||
      this.currentPage === null
    ) {
      this.currentPage = this.newPage();
      this.pageStartY = this.pageTopY;
      this.marginLeft = this.pdfOptions.pageSize.marginLeft;
      this.currentColumn = 0;
      return {
        page: this.currentPage,
        y: this.pageTopY,
        x: this.marginLeft,
        width: this.effectiveColumnWidth,
      };
    }
    this.currentColumn++;

    if (this.currentColumn >= this.pdfOptions.columnCount) {
      this.currentColumn = 0;
      this.currentPage = this.newPage();
      this.pageStartY = this.pageTopY;
    }
    const y = this.pageStartY ?? this.pageTopY;
    const x =
      this.pdfOptions.pageSize.marginLeft +
      this.currentColumn * this.columnWidth;
    this.marginLeft = x;
    if (this.getTrace()) {
      console.log(
        "new page",
        "margin Left",
        this.marginLeft,
        "column",
        this.currentColumn
      );
    }
    return {
      page: this.currentPage!,
      y,
      x,
      width: this.columnWidth,
    };
  }

  setTrace(trace: boolean): void {
    this.textWriter.setTrace(trace);
  }

  getTrace(): boolean {
    return this.textWriter.getTrace();
  }

  setRulesToTrace(rulesToTrace: string[]): void {
    this.rulesToTrace = rulesToTrace;
  }

  generateLinks(): void {
    this.rulesLinker.generateLinks();
  }

  setFullWidth(fullWidth: boolean): void {
    this.fullWidth = fullWidth;
  }

  /**
   * rendu d'affichage d'une règle simple type RULE, RP ou IA
   */
  drawRule({
    type = RuleType.RULE,
    rule,
    cleanTitle,
    currentY,
    currentPage,
    startY,
  }: {
    rule: RuleData;
    type?: RuleType;
    cleanTitle: string;
    currentY: number;
    currentPage: any | null;
    startY: number;
  }): {
    currentX: number;
    currentY: number;

    currentPage: any;
  } {
    // Initialize pdf-lib if needed
    initializePDFLib();

    const options = this.pdfOptions;
    let maxWidth = this.getMaxWidth();
    let marginLeft = this.getMarginLeft();
    if (!this.pdfDoc) {
      throw new Error("PDF document not initialized");
    }
    if (!currentPage) {
      const { page, x, y } = this.addPage();
      currentPage = page;
      marginLeft = x;
      currentY = y;
    }

    let fontSize = options.ruleFontSize;
    let lineSpacing = options.lineSpacing;
    if (type === RuleType.IA) {
      if (options.examples?.fontSize) {
        fontSize = options.examples?.fontSize;
      }
      if (options.examples?.lineSpacing) {
        lineSpacing = options.examples?.lineSpacing;
      }
    }

    // calculer le rendu d'affichage de la règle
    const cleanText = this.textWriter.stripHtml(rule.text);
    const dataSegments = this.textWriter.calculateFormattedTextWithWrap(
      cleanText,
      {
        fontSize: fontSize,
        lineSpacing: lineSpacing,
        fonts: options.fonts,
        maxWidth: maxWidth - options.textOffset,
        textAlign: options.textAlign,
        shiftSize: options.textOffset / 10,
      }
    );
    const minimumHeight =
      dataSegments.firstSegmentsHeight +
      options.ruleTitleFontSize * options.lineSpacing;

    if (this.getTrace()) {
      console.log(
        "currentY",
        currentY,
        "minimumHeight",
        Math.round(minimumHeight * 10) / 10,
        "bottom",
        currentY - minimumHeight
      );

      drawHorizontalLine({
        page: currentPage!,
        x: marginLeft,
        y: currentY - minimumHeight,
        width: maxWidth,
        color: rgb(0.8, 0, 0), // Red
      });
    }

    if (currentY - minimumHeight < options.pageSize.marginBottom) {
      const { page, x, y } = this.addPage();
      currentPage = page;
      currentY = y;
      marginLeft = x;
      if (this.getTrace()) {
        console.log(
          "new page",
          rule.id,
          ":",
          rule.title,
          "page",
          this.pdfDoc.getPages().length
        );
      }
    }

    // Autres types (règles normales, exemples, etc.)
    currentY -= options.ruleTitleFontSize;
    let titleText =
      (rule.type === RuleType.RULE || rule.type === RuleType.RP) && rule.id
        ? `${rule.id} - ${cleanTitle}`
        : cleanTitle;

    // Dessiner le titre de la règle
    currentY = this.textWriter.drawTitle(titleText, {
      page: currentPage!,
      x: marginLeft,
      y: currentY,
      font: options.fonts.bold,
      size: options.ruleTitleFontSize,
      maxWidth: maxWidth,
      lineSpacing: options.lineSpacing,
      align: "left",
    });

    // Store the page number (1-based) for the current rule
    this.rulesLinker?.addRulePageNumber(
      String(rule.id),
      this.pdfDoc.getPages().length,
      Math.round(
        (currentY + options.ruleTitleFontSize * options.lineSpacing) * 10
      ) / 10
    );

    currentY -= options.ruleTitleFontSize * options.lineSpacing;

    // Dessiner le texte de la règle avec formatage
    if (rule.text) {
      const result = this.textWriter.drawFormattedSegments(
        currentPage!,
        dataSegments.listSegments,
        {
          x: marginLeft + options.textOffset,
          y: currentY,
          startY: startY,
          fontSize: options.ruleFontSize * options.lineSpacing,
        },
        this.addPage.bind(this)
      );

      marginLeft = result.currentX;
      currentY = result.currentY;
      currentPage = result.currentPage;
    }

    return {
      currentX: marginLeft,
      currentY,
      currentPage,
    };
  }

  /**
   * ajouter un texte encadré avec fond gris clair
   */
  async addTextBlock({
    currentPage,
    options,
    rule,
    positionY,
    positionX,
    marginX = 4,
    paddingX = 12,
    paddingY = 7,
    width = 0,
    withTitle = false,
    withBox = true,
    bgColor = rgb(0.95, 0.95, 0.95),
    borderColor = rgb(0.8, 0.8, 0.8),
    radius = 8,
  }: {
    currentPage: any;
    options: PdfGeneratorOptions;
    rule: RuleData;
    positionX: number;
    positionY: number;
    marginX?: number;
    paddingX?: number;
    paddingY?: number;
    width?: number;
    withTitle?: boolean;
    withBox?: boolean;
    bgColor?: typeof rgb;
    borderColor?: typeof rgb;
    radius?: number;
  }): Promise<TextBlockResult> {
    // Initialize pdf-lib if needed
    initializePDFLib();

    const pageSize = this.pdfOptions.pageSize;

    if (width === 0) {
      width =
        pageSize.width -
        options.pageSize.marginLeft -
        options.pageSize.marginRight;
    }

    // chapter ecrire le texte dans un encadré avec fond gris clair
    if (rule.text) {
      const cleanText = this.textWriter.stripHtml(rule.text);
      const boxWidth = width;

      let boxHeight = paddingY * 2;
      if (withTitle) {
        boxHeight += options.ruleTitleFontSize * options.lineSpacing;
      }

      // Utiliser la nouvelle méthode de formatage pour le contenu
      const { listSegments, totalHeight } =
        this.textWriter.calculateFormattedTextWithWrap(cleanText, {
          fontSize: options.ruleFontSize,
          fonts: options.fonts,
          maxWidth: boxWidth - 2 * paddingX,
          lineSpacing: options.lineSpacing,
          textAlign: options.textAlign,
          shiftSize: options.textOffset / 10,
        });

      boxHeight += totalHeight;

      // Dessiner le rectangle de fond
      let boxX = positionX + marginX;
      let boxY = Math.round(positionY * 10) / 10;

      if (this.getTrace()) {
        console.log(
          "id ",
          rule.id,
          "boxX",
          boxX,
          "width",
          boxWidth,
          "boxHeight ",
          boxHeight,
          "boxY ",
          boxY,
          "totalHeight ",
          totalHeight,
          "bottom : ",
          Math.round((boxY - boxHeight) * 10) / 10
        );
        drawHorizontalLine({
          page: currentPage,
          x: boxX,
          y: boxY - boxHeight,
          width: boxWidth,
          color: rgb(0.8, 0, 0), // Red
        });
      }

      if (
        boxY - boxHeight < pageSize.marginBottom &&
        boxHeight < pageSize.height - pageSize.marginTop - pageSize.marginBottom
      ) {
        const { page, y, x } = this.addPage();
        currentPage = page;
        positionY = y;
        positionX = x;
        boxX = positionX + marginX;
        boxY = positionY;
        if (this.getTrace()) {
          console.log("box on new page for", rule.id, rule.title, boxY);
        }
      }

      if (withBox) {
        // Utiliser notre fonction pour coins arrondis
        drawRoundedRectangle({
          page: currentPage,
          x: boxX,
          y: boxY,
          width: boxWidth,
          height: boxHeight,
          radius: radius,
          fillColor: bgColor,
          borderColor: borderColor,
          borderWidth: 1,
          opacity: 0.8,
        });
      }

      // Dessiner le titre à l'intérieur de l'encadré
      let textY = positionY - paddingY - options.ruleFontSize;
      if (withTitle) {
        const size = options.ruleTitleFontSize;
        const truncatedTitle = truncateText(
          rule.title,
          boxWidth - paddingX,
          options.fonts.bold,
          size
        );
        currentPage.drawText(truncatedTitle, {
          x: boxX + paddingX,
          y: textY,
          size: size,
          font: options.fonts.bold,
          color: rgb(0, 0, 0),
        });
        textY -= size * options.lineSpacing;
      }

      // Utiliser la nouvelle méthode de formatage pour le contenu
      const result = this.textWriter.drawFormattedSegments(
        currentPage,
        listSegments,
        {
          x: boxX + paddingX,
          y: textY,
          bottomOffset: paddingY,
          startY: textY,
          fontSize: options.ruleFontSize,
        },
        this.addPage.bind(this)
      );

      if (this.getTrace()) {
        console.log("result", result);
      }

      currentPage = result.currentPage;
      positionY = result.currentY;
    }

    return { positionX, positionY, currentPage };
  }

  /**
   * Ajouter le header avec logo et titre
   */
  async addHeader({
    page = null,
    topPosition,
    currentChapter,
    radius = 5,
    small = false,
  }: HeaderOptions): Promise<HeaderResult> {
    // Initialize pdf-lib if needed
    initializePDFLib();

    // ajouter une page sauf pour la première page
    let currentPage: any | null = null;
    let marginLeft = this.pdfOptions.pageSize.marginLeft;
    if (page) {
      currentPage = page;
    } else {
      const { page, x } = this.addPage();
      currentPage = page;
      marginLeft = x;
    }
    let yPosition = topPosition;
    const pageSize = this.pdfOptions.pageSize;
    const options = this.pdfOptions;
    const logoWidth = small
      ? Math.round(options.logoWidth * 0.75)
      : options.logoWidth;
    const logoHeight = logoWidth * options.logoRatio;

    // Ajouter le logo si disponible
    if (this.logoBuffer) {
      const logoImage = await this.pdfDoc.embedPng(this.logoBuffer);

      currentPage.drawImage(logoImage, {
        x: marginLeft,
        y: yPosition - logoHeight,
        width: logoWidth,
        height: logoHeight,
      });
      yPosition -= logoHeight + 20;
    }

    // Ajouter le titre principal à droite du logo
    const leftBorder = marginLeft + logoWidth;
    const widthRemaining = pageSize.width - leftBorder - pageSize.marginRight;

    let font = options.fonts.bold;
    let titleFontSize = options.mainTitleFontSize;

    let titleWidth = font.widthOfTextAtSize(options.mainTitle, titleFontSize);
    while (widthRemaining < titleWidth) {
      if (--titleFontSize < 10) {
        break;
      }
      titleWidth = font.widthOfTextAtSize(options.mainTitle, titleFontSize);
    }

    let x =
      leftBorder +
      this.textWriter.alignCenter(
        options.mainTitle,
        font,
        titleFontSize,
        widthRemaining
      );
    let yLeftPosition = topPosition - titleFontSize;

    currentPage.drawText(options.mainTitle, {
      x,
      y: yLeftPosition,
      size: titleFontSize,
      font: font,
      color: rgb(0, 0, 0.3),
    });

    if (currentChapter) {
      const fontSize = Math.round(titleFontSize * 0.8);
      yLeftPosition -= titleFontSize * options.lineSpacing;

      x =
        leftBorder +
        this.textWriter.alignCenter(
          currentChapter.title,
          font,
          fontSize,
          widthRemaining
        );
      currentPage.drawText(currentChapter.title, {
        x,
        y: yLeftPosition,
        size: fontSize,
        font: font,
        color: rgb(0, 0, 0.3),
      });
    }

    if (this.rulesData.meta?.author && !small) {
      yLeftPosition -= titleFontSize * options.lineSpacing;
      let author = this.textWriter.stripHtml(this.rulesData.meta.author);
      this.textWriter.drawTitle(author, {
        page: currentPage,
        x: leftBorder,
        y: yLeftPosition,
        font: options.fonts.italic,
        size: Math.round(titleFontSize * 0.7),
        maxWidth: widthRemaining,
        lineSpacing: options.lineSpacing,
        color: rgb(0.2, 0.2, 0.2),
        opacity: 0.6,
        align: "center",
      });
      yLeftPosition -= titleFontSize * 0.7 * options.lineSpacing;
    }

    yPosition = Math.min(yPosition, yLeftPosition) - 4;

    // chapter ecrire le texte dans un encadré avec fond gris clair
    if (currentChapter?.text && !small) {
      const textBlock = await this.addTextBlock({
        currentPage,
        options,
        rule: currentChapter,
        positionY: yPosition,
        positionX: pageSize.marginLeft,
        marginX: 2,
        width: pageSize.width - pageSize.marginLeft - pageSize.marginRight - 4,
        radius,
        bgColor: rgb(0.9, 0.9, 0.99),
      });
      yPosition = textBlock.positionY;
      currentPage = textBlock.currentPage;
    }

    return { yPosition, currentPage };
  }

  /**
   * Glossary exists from the 2026 rules onward.
   */
  private shouldIncludeGlossary(): boolean {
    const options = this.pdfOptions;
    return (
      options.includeGlossary != false && Number(options.version) >= 2026
    );
  }

  /**
   * Génère les règles du document.
   * glossaryPass renders only the glossary, after the examples appendix.
   */
  async generateRules(
    glossaryPass = false
  ): Promise<{ currentY: number; currentPage: any }> {
    const options = this.pdfOptions;
    const startY = options.pageSize.height - options.pageSize.marginTop;

    let currentY = startY;
    let marginLeft = options.pageSize.marginLeft;
    let currentPage: any | null = null;

    const maxWidth = this.getMaxWidth();

    if (options.chapterFontSize <= 0) options.chapterFontSize = 16;

    // Itérer directement sur l'array ordonné des règles
    for (const rule of this.rulesData.rules) {
      // Filtrer selon les options
      if (!options.includeProcedures && rule.type === RuleType.RP) continue;
      if (
        !(options.includeExamples && options.examples?.inline) &&
        rule.type === RuleType.IA
      )
        continue;
      if (rule.type === RuleType.HEADING && rule.id === "H99") continue;
      const isGlossary = String(rule.id).startsWith("GL");
      if (glossaryPass !== isGlossary) continue;
      if (isGlossary && !this.shouldIncludeGlossary()) continue;
      if (rule.type === RuleType.INFO) {
        if (rule.id === "PDF") continue;
      }
      if (rule.type === RuleType.CHAPTER) {
        if (String(rule.id).startsWith("RP") && !options.includeProcedures) {
          continue;
        }
      }

      // Vérifier si on a besoin d'une nouvelle page
      if (
        rule.type !== RuleType.CHAPTER &&
        (!currentPage || currentY < options.pageSize.marginBottom * 2)
      ) {
        if (this.getTrace()) {
          console.log("new page", rule.id, ":", rule.title);
        }
        const { page, y, x } = this.addPage();
        currentPage = page;
        currentY = y;
        marginLeft = x;
      }

      if (this.rulesToTrace && this.rulesToTrace.includes(rule.id)) {
        console.log("trace", rule.id, ":", rule.title);
        this.setTrace(true);
      } else {
        this.setTrace(false);
      }

      // Nettoyer et préparer le texte
      const cleanTitle = cleanTextForPdf(rule.title || "");

      this.textWriter.setId(rule.id);

      // Traitement différent selon le type
      switch (rule.type) {
        case RuleType.CHAPTER:
          // Ajouter le header
          this.setFullWidth(true);
          const header = await this.addHeader({
            topPosition: startY,
            currentChapter: rule,
            small:
              options.includeIndex &&
              options.indexAt === "start" &&
              rule.id == "TOP",
          });
          this.setFullWidth(false);
          currentY = header.yPosition - options.ruleFontSize;
          this.setPageStartY(currentY);
          currentPage = header.currentPage;
          break;

        case RuleType.HEADING:
          if (
            currentY <
            options.pageSize.marginBottom +
              (options.ruleFontSize + options.lineSpacing) * 10
          ) {
            const { page, y, x } = this.addPage();
            currentPage = page;
            currentY = y;
            marginLeft = x;
          }

          currentY -= options.chapterFontSize * options.lineSpacing;

          let headerTitle = cleanTitle.toUpperCase();

          currentY = this.textWriter.drawTitle(headerTitle, {
            page: currentPage!,
            x: marginLeft,
            y: currentY,
            font: options.fonts.bold,
            size: options.chapterFontSize,
            maxWidth: maxWidth,
            lineSpacing: options.lineSpacing,
            align: "center",
          });

          this.rulesLinker?.addRulePageNumber(
            String(rule.id),
            this.pdfDoc.getPages().length,
            Math.round((currentY + options.chapterFontSize) * 10) / 10
          );

          currentY -= options.chapterFontSize;
          break;

        case RuleType.IA:
          if (options.includeExamples) {
            currentY -= Number(options.ruleFontSize);
            const textBlock = await this.addTextBlock({
              currentPage: currentPage!,
              options,
              rule: rule,
              positionY: currentY + 10,
              withTitle: true,
              positionX: this.getMarginLeft(),
              marginX: options.examples?.marginX || 0,
              width: this.getMaxWidth() - 2 * (options.examples?.marginX || 0),
              paddingX: 10,
              paddingY: 8,
              radius: options.examples?.radius || 12,
              bgColor: options.examples?.bgColor
                ? hexToRgb(options.examples.bgColor)
                : rgb(0.99, 0.99, 0.8),
              borderColor: options.examples?.borderColor
                ? hexToRgb(options.examples.borderColor)
                : rgb(0.9, 0.9, 0.5),
            });

            currentY = textBlock.positionY - (options.examples?.marginY || 10);
            marginLeft = textBlock.positionX;
            currentPage = textBlock.currentPage;
          }
          break;

        default:
          // calculer le rendu d'affichage de la règle
          const result = this.drawRule({
            rule,
            cleanTitle: cleanTitle,
            currentY,
            currentPage,
            startY,
          });
          marginLeft = result.currentX;
          currentY = result.currentY;
          currentPage = result.currentPage;
          break;
      }
    }

    return { currentY, currentPage: currentPage! };
  }

  /**
   * Glossary chapter, after the examples appendix.
   */
  async addGlossary(): Promise<void> {
    if (!this.shouldIncludeGlossary()) return;
    await this.generateRules(true);
  }

  /**
   * Ajouter l'annexe avec les exemples
   */
  async addAppendix(): Promise<void> {
    const options = this.pdfOptions;
    if (!options.includeExamples || options.examples?.inline) {
      return;
    }
    const pageSize = options.pageSize;

    const startY = pageSize.height - pageSize.marginTop;

    const rulesData = this.rulesData;
    // inclure les exemples en fin de document

    // récupèrer le mot de la règle
    const ruleTitle = cleanTextForPdf(rulesData.meta?.rule_title || "Rule");
    // ajoutons un header avec une structure règle fictive
    this.setFullWidth(true);
    const header = await this.addHeader({
      topPosition: startY,
      currentChapter: {
        id: "EXAMPLES",
        title: rulesData.meta?.annexe || "Annexe",
        text: "",
        type: RuleType.CHAPTER,
      },
    });
    this.setFullWidth(false);
    let currentY = header.yPosition - options.ruleFontSize;
    let currentPage = header.currentPage;
    this.setPageStartY(currentY);

    const examples = rulesData.rules.filter(
      (rule: RuleData) => rule.type === RuleType.IA
    );
    for (const example of examples) {
      // supprimer 1 ou 2 digit au début de l'id
      const ruleNumber = example.id.substring(2);
      let cleanTitle = cleanTextForPdf(example.title || "");
      if (!example.title.startsWith(ruleTitle)) {
        cleanTitle = ruleTitle + " " + ruleNumber + " : " + cleanTitle;
      }

      this.textWriter.setId(example.id);

      const result = this.drawRule({
        rule: example,
        type: RuleType.IA,
        cleanTitle: cleanTitle,
        currentY,
        currentPage,
        startY,
      });

      currentY = result.currentY;
      currentPage = result.currentPage;
    }

    return;
  }

  /**
   * Génère l'index du document
   */
  async generateIndex(): Promise<void> {
    if (!this.pdfDoc) {
      throw new Error("PDF document not initialized");
    }
    let currentPage: any | null = null;
    const options = this.pdfOptions;
    const startY = options.pageSize.height - options.pageSize.marginTop;

    // Paramètres de l'index
    const indexOptions = options.indexOptions || {
      lineSpacing: 1.1,
      titleRuleSize: 10,
      sectionRuleSize: 12,
      chapterRuleSize: 13,
      columnCount: 2,
      fontFamily: "TimesRoman",
      fonts: {
        regular: options.fonts.regular,
        bold: options.fonts.bold,
      },
    };

    let pageCount = 0;

    // console.log("Genera:teIndex at", options.indexAt);
    if (options.indexAt === "start") {
      currentPage = this.pdfDoc.insertPage(pageCount, [
        options.pageSize.width,
        options.pageSize.height,
      ]);
      pageCount++;
    } else {
      currentPage = this.pdfDoc.addPage([
        options.pageSize.width,
        options.pageSize.height,
      ]);
    }

    const rulesData = this.rulesData;
    let topRule = rulesData.rules.find((rule) => rule.id === "TOP");

    // Ajoutons un header avec une structure règle fictive pour l'index
    const header = await this.addHeader({
      page: currentPage,
      topPosition: startY,
      currentChapter: topRule,
      small: options.indexAt === "end",
    });
    let initialY = header.yPosition - indexOptions.chapterRuleSize;
    currentPage = header.currentPage;

    // Calculer les dimensions pour les colonnes
    const pageWidth =
      options.pageSize.width -
      options.pageSize.marginLeft -
      options.pageSize.marginRight;
    const columnWidth = pageWidth / indexOptions.columnCount;
    const columnGap = 12;
    const effectiveColumnWidth = columnWidth - columnGap;
    let columnX = options.pageSize.marginLeft;

    // Préparer les données d'index (exclure les exemples inline)
    const indexData: { chapter: string; rules: RuleData[] }[] = [];
    let currentChapter = "";
    let currentRules: RuleData[] = [];

    for (const rule of rulesData.rules) {
      // Exclure les exemples inline et certains types
      if (rule.type === RuleType.IA && options.examples?.inline) continue;
      if (rule.type === RuleType.HEADING && rule.id === "H99") continue;
      if (rule.type === RuleType.INFO && rule.id === "PDF") continue;
      if (!this.shouldIncludeGlossary() && String(rule.id).startsWith("GL"))
        continue;

      if (rule.type === RuleType.CHAPTER) {
        // Sauvegarder le chapitre précédent
        if (currentChapter && currentRules.length > 0) {
          indexData.push({ chapter: currentChapter, rules: [...currentRules] });
        }
        currentChapter = rule.title;
        currentRules = [];
      } else if (
        rule.type === RuleType.RULE ||
        rule.type === RuleType.HEADING ||
        (rule.type === RuleType.RP && options.includeProcedures) ||
        (rule.type === RuleType.IA &&
          options.includeExamples &&
          !options.examples?.inline)
      ) {
        currentRules.push(rule);
      }
    }

    // Ajouter le dernier chapitre
    if (currentChapter && currentRules.length > 0) {
      indexData.push({ chapter: currentChapter, rules: [...currentRules] });
    }

    // Générer l'index en colonnes
    let currentColumn = 0;
    let columnY = initialY;
    let bottomSpacing = 0;

    columnY = initialY;

    const oneCharSize =
      indexOptions.fonts?.regular.widthOfTextAtSize(
        "0",
        indexOptions.titleRuleSize
      ) || 8;

    // Dessiner les règles du chapitre
    for (const rule of rulesData.rules) {
      // Exclure les exemples inline et certains types
      if (rule.type === RuleType.IA) continue;
      if (rule.type === RuleType.HEADING && rule.id === "H99") continue;
      if (rule.type === RuleType.INFO && rule.id === "PDF") continue;
      if (!this.shouldIncludeGlossary() && String(rule.id).startsWith("GL"))
        continue;
      if (
        !options.includeProcedures &&
        (rule.type === RuleType.RP ||
          (rule.type === RuleType.CHAPTER && rule.id === "RP"))
      )
        continue;

      let fontSize = indexOptions.titleRuleSize;
      let fontType = indexOptions.fonts?.regular || options.fonts.regular;

      let expectedSpace = 0;
      let topSpacing = 0;
      let spacing = 0;
      let truncateWidth = effectiveColumnWidth;

      if (rule.type === RuleType.CHAPTER) {
        fontSize = indexOptions.chapterRuleSize;
        fontType = indexOptions.fonts?.bold || options.fonts.bold;
        topSpacing = fontSize * 0.6;
        spacing = Math.max(topSpacing, bottomSpacing); // previous bottom spacing compared to top current spacing
        bottomSpacing = fontSize * 0.3;
        expectedSpace =
          (indexOptions.sectionRuleSize * 1.5 +
            4 * indexOptions.titleRuleSize) *
          indexOptions.lineSpacing;
      } else if (rule.type === RuleType.HEADING) {
        fontSize = indexOptions.sectionRuleSize;
        fontType = indexOptions.fonts?.bold || options.fonts.bold;
        topSpacing = fontSize * 0.6;
        spacing = Math.max(topSpacing, bottomSpacing); // previous bottom spacing compared to top current spacing
        bottomSpacing = fontSize * 0.2;
        expectedSpace =
          4 * indexOptions.titleRuleSize * indexOptions.lineSpacing;
      } else {
        spacing = bottomSpacing;
        bottomSpacing = 0;
        truncateWidth = effectiveColumnWidth - 35;
      }
      if (columnY === initialY) {
        columnY -= fontSize;
      } else {
        columnY -= (fontSize + spacing) * indexOptions.lineSpacing;
      }

      // Vérifier si on a assez de place
      if (columnY - expectedSpace < options.pageSize.marginBottom) {
        currentColumn++;
        if (currentColumn >= indexOptions.columnCount) {
          if (options.indexAt === "start") {
            currentPage = this.pdfDoc.insertPage(pageCount, [
              options.pageSize.width,
              options.pageSize.height,
            ]);
            pageCount++;
          } else {
            currentPage = this.pdfDoc.addPage([
              options.pageSize.width,
              options.pageSize.height,
            ]);
          }
          currentColumn = 0;
          initialY = startY;
          columnX = options.pageSize.marginLeft;
        } else {
          columnX =
            options.pageSize.marginLeft +
            currentColumn * (columnWidth + columnGap);
        }
        columnY = initialY - fontSize;
      }
      columnY = Math.round(columnY);
      columnX = Math.round(columnX);

      let offsetX = 0;

      // Titre de la règle
      let ruleTitle = cleanTextForPdf(rule.title);
      if (rule.type === RuleType.RULE || rule.type === RuleType.RP) {
        if (rule.type == RuleType.RULE) {
          if (Number(rule.id) <= 9) {
            offsetX = oneCharSize;
            truncateWidth -= offsetX;
          }
          ruleTitle = `${rule.id} - ${ruleTitle}`;
        } else {
          let rId = rule.id;
          if (rule.id.length <= 3) {
            rId = rId.substring(0, 2) + "-" + rId.substring(2);
          }

          ruleTitle = `${rId} - ${ruleTitle}`;
        }
      }

      const truncatedTitle = truncateText(
        ruleTitle,
        truncateWidth,
        fontType,
        fontSize
      );

      const titleWidth = Math.round(
        fontType.widthOfTextAtSize(truncatedTitle, fontSize)
      );

      currentPage?.drawText(truncatedTitle, {
        x: columnX + 10 + offsetX,
        y: columnY,
        size: fontSize,
        font: fontType,
        color: rgb(0.3, 0.3, 0.3),
      });

      const pageNumber = this.rulesLinker?.getRulePageNumber(String(rule.id));

      if (pageNumber) {
        this.rulesLinker?.addRuleToLink({
          ruleId: rule.id,
          page: this.pdfDoc.getPages().indexOf(currentPage!) + 1,
          x: columnX,
          y: columnY,
          width: titleWidth,
          height: fontSize,
          withUnderline: false,
          insertedPages: true,
        });

        // Simuler un numéro de page (pour le moment, on utilise juste un placeholder)
        if (rule.type !== RuleType.CHAPTER && rule.type !== RuleType.HEADING) {
          const pageNumberString = String(pageNumber.page);
          const pageNumberWidth = fontType.widthOfTextAtSize(
            pageNumberString,
            fontSize
          );
          currentPage?.drawText(pageNumberString, {
            x: columnX + effectiveColumnWidth - pageNumberWidth,
            y: columnY,
            size: fontSize,
            font: fontType,
            color: rgb(0.5, 0.5, 0.5),
          });

          // Dessiner des points entre le titre et le numéro de page
          const titleWidth = fontType.widthOfTextAtSize(
            truncatedTitle,
            indexOptions.titleRuleSize
          );
          const dotsStart = columnX + 10 + titleWidth + 5;
          const dotsEnd = columnX + effectiveColumnWidth - pageNumberWidth - 5;
          const dotSpacing = 8;

          for (let x = dotsStart; x < dotsEnd; x += dotSpacing) {
            currentPage?.drawText(".", {
              x: x,
              y: columnY,
              size: fontSize,
              font: options.fonts.regular,
              color: rgb(0.7, 0.7, 0.7),
            });
          }
        }
      }
    }
    this.rulesLinker?.setNumberInsertedPages(pageCount);
  }
}
